import React, { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AiJob, useAiJobs } from "../../hooks/useAiWork";
import classes from "./assistant.module.scss";

const STILL_WORKING_AFTER_MS = 15000;

const elapsed = (ms: number): string => {
    const seconds = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/**
 * Locks the area an AI button works on: nobody can type into what the AI is
 * about to fill. A cover shows what it's doing and for how long.
 */
const AiCover: React.FC<{ job: AiJob; scope: HTMLElement; now: number }> = ({ job, scope, now }) => {
    // a scrolling area (a drawer body) is covered where it's scrolled to when it locks; it can't scroll while locked
    const [box] = useState(() => (scope.scrollHeight > scope.clientHeight ? { top: scope.scrollTop, height: scope.clientHeight } : { top: 0, bottom: 0 }));

    useLayoutEffect(() => {
        const isStatic = getComputedStyle(scope).position === "static";
        const hadFocus = scope.contains(document.activeElement);
        if (isStatic) scope.style.position = "relative";
        scope.setAttribute("inert", "");
        scope.setAttribute("aria-busy", "true");
        return () => {
            if (isStatic) scope.style.position = "";
            scope.removeAttribute("inert");
            scope.removeAttribute("aria-busy");
            // locking dropped the focus; give it back to the button
            if (hadFocus && job.button.isConnected && document.activeElement === document.body) job.button.focus({ preventScroll: true });
        };
    }, [scope, job.button]);

    const ms = now - job.startedAt;
    return createPortal(
        <div className={classes.aiCover} style={box} data-testid="ai-cover">
            <div className={classes.aiCoverStrip}>
                <i className="bi bi-stars" aria-hidden="true" />
                <span className={classes.aiCoverText}>
                    <b className={classes.aiCoverLabel}>{job.label || "AI"}</b>
                    <span className={classes.aiCoverMeta}>
                        AI working · <span className={classes.aiTime}>{elapsed(ms)}</span>
                        {ms > STILL_WORKING_AFTER_MS && " · Still working. Long ones can take up to a minute."}
                    </span>
                </span>
            </div>
            <div className={classes.aiCoverLines} aria-hidden="true"><i /><i /><i /><i /></div>
        </div>,
        scope,
    );
};

/**
 * "AI working · 0:12" in the header while an AI button works (one at a time),
 * plus the lock on the area it works on. Renders nothing otherwise.
 */
const AiWorking: React.FC<{ isCompact?: boolean }> = ({ isCompact }) => {
    const jobs = useAiJobs();
    const [now, setNow] = useState(Date.now);
    const isWorking = jobs.length > 0;

    useEffect(() => {
        if (!isWorking) return;
        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [isWorking]);

    const job = jobs[0];
    return (
        <>
            <span className="visually-hidden" role="status">{job ? `AI working: ${job.label}` : ""}</span>
            {job && (
                <button type="button" className={`btn-unstyled ${classes.aiWorkingChip}`}
                    onClick={() => (job.scope ?? job.button).scrollIntoView({ behavior: "smooth", block: "center" })}
                    title={`The AI is working on “${job.label}”. Go to it.`}>
                    <i className="bi bi-stars" aria-hidden="true" />
                    {!isCompact && "AI working · "}
                    <span className={classes.aiTime}>{elapsed(now - job.startedAt)}</span>
                </button>
            )}
            {jobs.map((j) => j.scope && <AiCover key={j.id} job={j} scope={j.scope} now={now} />)}
        </>
    );
};

export default AiWorking;
